/*
** flat drawing -- draw a 1d quantum wave as a 2d bargraph (band across top)
** Copyright (C) 2021-2026 Tactile Interactive, all rights reserved
*/

import abstractDrawing from './abstractDrawing.js';
import {drawingUniform, drawingAttribute} from './drawingVariable.js';
import cx2rygb from './cx2rygb/cx2rygb.glsl.js';
import qeFuncs from '../engine/qeFuncs.js';
import qeConsts from '../engine/qeConsts.js';

let traceAvatarBeforeDrawing = false;
let traceAvatarAfterDrawing = false;
let traceMaxHeight = false;
let traceFlatDrawing = false;
let traceViewport = false;
let traceReloadRow = false;

// diagnostic purposes; draws more per vertex
let traceDrawPoints = false;
let traceDrawLines = false;

let displayWrapEdges = false;  // soon to be a pref

/* ******************************************************* flat drawing */
// The glsl sources for webgl drawing

/*
** data format of attributes:  four column table of floats
** 𝜓.re  𝜓.im	  (unused)	 serial.
** uses gl_VertexID	  NO! that's opengl 2 only
** to figure out whether the y should be re^2+im^2 or zero
*/

// make the line number for the start correspond to this JS file line number - the NEXT line
const vertexShaderSrc = `// flat drawing vertex
${cx2rygb}
#line 38
varying highp vec4 vColor;
attribute vec4 row;
uniform float barWidth;
uniform float maxHeight;

void main() {
	int vertexSerial = int(row.w);
	bool odd = int(vertexSerial) / 2 * 2 < vertexSerial;

	// figure out y
	float y;
	if (odd) {
		y = (row.x * row.x + row.y * row.y) / maxHeight;
	}
	else {
		y = 0.;	 // top of the screen
	}
	y = 1. - 2. * y;

	// figure out x, basically the point index; map to -1...+1
	float x;
	x = float(int(vertexSerial) / 2) * barWidth * 2. - 1.;

	gl_Position = vec4(x, y, 0., 1.);

	//	for the color, convert the complex values via this algorithm
	//vColor.rgb = cx2rygb(row.xy);
	//vColor.rgb = cx2rygb(vec2(row.x, row.y));
	//vColor.a = 1.;
	vColor = vec4(cx2rygb(vec2(row.x, row.y)), 1.);

	// make the colors darker toward zero (top)
	if (!odd)
		vColor = vec4(vColor.r/2., vColor.g/2., vColor.b/2., vColor.a);

	// dot size, in pixels not clip units. actually a fuzzy square.
	gl_PointSize = 10.;
}
`;

const fragmentShaderSrc = `// flat drawing frag
#line 80
precision highp float;
varying highp vec4 vColor;

void main() {
	gl_FragColor = vColor;
}
`;

// the original display that's worth watching: flat upside down hump graph
export class flatDrawing extends abstractDrawing {
	constructor(scene) {
		super(scene, 'flatDrawing');

		//this.space = space;

		// each point in the wave results in two vertices, top and wave.
		// And each of those is four single floats going to the GPU
		this.avatar = scene.avatar;
		this.avatar.attachViewBuffer(this.scene.flatAvatarID, null, 4, this.space.nPoints * 2, 'row');

		this.vertexShaderSrc = vertexShaderSrc;
		this.fragmentShaderSrc = fragmentShaderSrc;
	}

	relaxHighest = () => {
		if (!this.maxHeight) {
			// first time around
			this.maxHeight = this.avatar.double0;
			return;
		}
		let ratio = Math.abs(this.avatar.double0 / this.maxHeight);
		if (ratio > 1) {
			// too high - fix it immediately
			this.maxHeight = (this.maxHeight * 15 + this.avatar.double0) / 16;
			//this.maxHeight = this.avatar.double0;
			return;
		}

		if (ratio < .5) {
			//dblog(4);
			this.maxHeight = (this.maxHeight * 3 + this.avatar.double0) / 4;
		}
		else if (ratio < .9) {
			//dblog(16);
			this.maxHeight = (this.maxHeight * 15 + this.avatar.double0) / 16;
		}
		else {
			//dblog(256);
			this.maxHeight = (this.maxHeight * 255 + this.avatar.double0) / 256;
		}
	}

	figureBarWidth() {
		const space = this.space;
		if (space.continuum == qeConsts.contENDLESS) {
			// eg for N=8, 8 segments and segment 0===8 and 1===9
			this.barWidth = 1 / space.nStates;
			this.vertexCount = (space.nPoints - 1) * 2;	 // nStates * vertsPerBar
		}
		else if (space.continuum == qeConsts.contWELL) {
			// eg for N=8, 8 segments plus two on ends that go to ∞,
			// so segment 0 === 9 = ∞
			this.barWidth = 1 / (space.nStates + 1);
			this.vertexCount = space.nPoints * 2;	 // nStates * vertsPerBar
		}
		else {
			throw `bad space continuum ${space.continuum}`;
		}
	}

	// loads view buffer from corresponding wave, calculates highest norm.
	// one time set up of variables for this drawing, every time canvas and scene is recreated
	createVariables() {
		this.gl.useProgram(this.program);
		if (traceFlatDrawing)
			console.log(`♭♭♭ flatDrawing ${this.sceneName}: creatingVariables`);

		this.figureBarWidth();

		this.maxHeightUniform = new drawingUniform('maxHeight', this,
			() => {
				// fresh out of the loader, maxHeight wobbles up and down. Smooth it.
				if (!this.maxHeight) {
					// first time around
					this.maxHeight = this.avatar.double0;
				}
				else {
					// relax changes.  how	quickly? depends on how fast it's changing
					this.relaxHighest();

					//this.maxHeight = this.avatar.double0;
					//this.maxHeight = (this.maxHeight * 15 + this.avatar.double0) / 16;
				}

				if (traceMaxHeight)
					console.log(`♭♭♭ flatDrawing reloading outer:  `
						+` maxHeight=${this.avatar.double0.toFixed(5)} `);

				return {value: this.maxHeight * this.scene.PADDING_ON_BOTTOM, type: '1f'};
			}
		);


		// WELL continuum:	potential at the ends of the well are infinite; so
		// psi on the border points is zero. These are the boundary datapoints,
		// so for N=8, 10 edges between 9 bars, 7 between and 2 on ends.

		// for ENDLESS, we wrap around one bar, so if N=8, there's two border bar at 0 and 8.
		// there's 7 bars between. 9 bars total, 10 edges, matching the 10 = nPoints
		// So, the same for WELL and ENDLESS

		let {start, end} = this.space;
		//let barWidth = this.space.vDisp.barWidth;
		// just testing  barWidth /= 2;

		let nPoints = this.nPoints = this.space.nPoints;
		let nStates = this.space.nStates;
		this.barWidthUniform = new drawingUniform('barWidth', this,
			() => {
				if (!isFinite(this.barWidth)) {
					debugger;
					throw `barWidth not finite: ${this.barWidth}`;
				}
				return {value: this.barWidth, type: '1f' };
			}
		);
		if (traceFlatDrawing) console.log(`♭♭♭ barWidth frac of 1= ${this.barWidth}`);

		this.rowFloats = 4;
		new drawingAttribute('row', this, this.rowFloats,
			() => {
			//debugger;
			let  cav = this.scene.paintingNeeds.cavity;
			cav.fixBoundaries();
			qeFuncs.avatar_avFlatLoader(this.avatar._pointer_, this.scene.flatAvatarID,
					cav._pointer_, nPoints);

			if (traceReloadRow) {
				console.log(`♭♭♭ flatDrawing  ${this.avatarLabel}: at row getViewBuffer() `
					+` loading to ${this.avatar.label}	 this.vertexCount=${this.vertexCount} `
					+` total floats=${this.vertexCount * this.rowFloats}  double0=$ {this.avatar.double0}`);
			}

			return this.avatar.getViewBuffer(this.scene.flatAvatarID);
		});

	}

	draw(width, height) {
		if (traceFlatDrawing) {
			console.log(`♭♭♭ flat Drawing  ${this.avatarLabel}: `
				+` width=${width}, height=${height}	 drawing ${this.vertexCount/2} points `
				+` maxHeight=${this.maxHeight}`);
		}
		const gl = this.gl;

		if (traceAvatarBeforeDrawing)
			this.avatar.dumpComplexViewBuffer(this.scene.flatAvatarID, this.nPoints,
					`♭♭♭ before drawing in flatDrawing.js`);

		// remember for endless: we draw N+1 bars with N+2 edges (2 on edges)
		gl.drawArrays(gl.TRIANGLE_STRIP, 0, this.vertexCount);
		if (traceFlatDrawing) {
			console.log(`♭♭♭just drewArays-flat on avatar ptr=${this.avatar._pointer_} `
				+` this.avatar.label=${this.avatar.label}, `
				+` buffer label=${this.avatar.bufferNames[this.scene.flatAvatarID]}`);
		}

		if (traceDrawLines) {
			gl.lineWidth(1);  // it's the only option anyway

			gl.drawArrays(gl.GL_LINE_STRIP, 0, this.vertexCount);
		}

		if (traceDrawPoints)
			gl.drawArrays(gl.POINTS, 0, this.vertexCount);

		// i think this is problematic
		if (traceAvatarAfterDrawing) {
			this.avatar.dumpMeta(`traceAvatarAfterDrawing:`);
			this.avatar.dumpEachViewBuffer(1, `done drawing flat`);
			this.avatar.dumpComplexViewBuffer(this.scene.flatAvatarID,
				this.nPoints, `♭♭♭ finished drawing in flatDrawing.js`);
			console.log(`♭♭♭ barWidthUniform=`, this.barWidthUniform.reloadFunc(),
				+` maxHeightUniform=`, this.maxHeightUniform.reloadFunc());
		}
	}
}

export default flatDrawing;

